/**
 * ScenePanel — Scene-Verwaltung im Sidebar
 *
 * Zeigt alle Scenes der aktuellen Punktwolke.
 * Erlaubt Erstellen, Aktivieren (Viewer-Zustand laden),
 * View aktualisieren und Löschen.
 * Scenes können als Startscene definiert werden.
 * Scenes speichern den Ansichtsmodus (Modell/Grundriss/Schnitt).
 */

import { useState, useMemo, useEffect, useRef, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { useSceneStore } from '../../store/scenes'
import { useAnnotationStore } from '../../store/annotations'
import type { Scene } from '../../types/annotations'
import { captureAndStoreSceneThumbnail } from '../../lib/thumbnailService'
import { captureViewerThumbnailPair } from '../../lib/captureViewerThumbnail'
import { captureViewerThumbnailMediumHigh } from '../../lib/captureViewerThumbnail'

// ─── Helpers ─────────────────────────────────────────────────────────────────

declare global {
  interface Window {
    viewer: any
    currentClipVolume: any
    captureSceneViewerState?: () => any
    restoreSceneViewerState?: (vs: any) => void
    getCurrentViewMode?: () => string
    getActiveViewTargetName?: () => string | null
    activateSceneViewMode?: (mode: string, target: string | null) => void
    _activateStartScene?: (projectId?: string) => void
  }
}

function captureViewerState() {
  // Neuer vollständiger Capture (Renderer, Pro-Cloud, WMS-Layer)
  if ((window as any).captureFullViewerState) {
    return (window as any).captureFullViewerState()
  }
  // Fallback: nur Kamera + ClipBox (Rückwärtskompatibilität)
  const view = (window as any).viewer?.scene?.view
  if (!view) return null
  const pivot = view.getPivot?.() ?? view.position
  const camera = {
    position: { x: view.position.x, y: view.position.y, z: view.position.z },
    target: { x: pivot.x, y: pivot.y, z: pivot.z },
  }
  const cv = (window as any).currentClipVolume
  const clipBox = cv ? {
    position: [cv.position.x, cv.position.y, cv.position.z] as [number, number, number],
    scale: [cv.scale.x, cv.scale.y, cv.scale.z] as [number, number, number],
    rotation: [cv.rotation.x, cv.rotation.y, cv.rotation.z] as [number, number, number],
  } : null
  return { camera, clipBox }
}

const VIEW_MODE_LABELS: Record<string, string> = {
  modell: 'Modell',
  grundriss: 'Grundriss',
  schnitt: 'Schnitt',
}

// ─── SceneRow ────────────────────────────────────────────────────────────────

function SceneRow({ scene, isActive, isStartScene, isDetailsOpen, onActivate, onUpdateView, onDelete, onToggleStart, onToggleDetails }: {
  scene: Scene
  isActive: boolean
  isStartScene: boolean
  isDetailsOpen: boolean
  onActivate: () => void
  onUpdateView: () => void
  onDelete: () => void
  onToggleStart: () => void
  onToggleDetails: () => void
}) {
  const updateScene = useSceneStore(s => s.updateScene)
  const [editing, setEditing] = useState(false)
  const [title, setTitle] = useState(scene.title)
  const [menuOpen, setMenuOpen] = useState(false)
  const [hovered, setHovered] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Menü bei Klick außerhalb schließen
  useEffect(() => {
    if (!menuOpen) return
    function handler(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [menuOpen])

  const viewMode = scene.viewMode || 'modell'
  const viewLabel = VIEW_MODE_LABELS[viewMode] || 'Modell'

  const viewBadgeClass = {
    modell: 'ghost-view-badge ghost-view-badge--modell',
    grundriss: 'ghost-view-badge ghost-view-badge--grundriss',
    schnitt: 'ghost-view-badge ghost-view-badge--schnitt',
  }[viewMode] || 'ghost-view-badge ghost-view-badge--modell'

  const stop = (e: React.MouseEvent) => e.stopPropagation()

  const showHoverActions = hovered || menuOpen || isActive

  return (
    <div
      className={`ghost-item ${isActive ? 'ghost-item--scene-active' : ''}`}
      data-section="scene-row"
      data-scene-id={scene.id}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => { if (!editing) onActivate() }}
      style={{
        position: 'relative',
        display: 'block',
        width: '100%',
        boxSizing: 'border-box',
        padding: '10px 12px 10px 14px',
        borderBottom: '1px solid #d1d5db',
        background: hovered ? 'rgba(0,0,0,0.04)' : 'transparent',
        cursor: 'pointer',
        transition: 'background 0.12s ease',
        color: '#111827',
      }}
    >
      {/* Linker Akzentstreifen für aktive Scene */}
      {isActive && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 3,
            background: '#3b82f6',
          }}
        />
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 28, width: '100%' }}>
        {/* Thumbnail */}
        {scene.thumbnailSmall ? (
          <div style={{
            flexShrink: 0,
            width: 44,
            height: 30,
            borderRadius: 4,
            overflow: 'hidden',
            background: '#1f2937',
          }}>
            <img
              src={scene.thumbnailSmall}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </div>
        ) : (
          <div style={{
            flexShrink: 0,
            width: 44,
            height: 30,
            borderRadius: 4,
            background: '#e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#9ca3af',
            fontSize: 14,
          }}>
            ◆
          </div>
        )}

        {/* Star */}
        <button
          onClick={(e) => { stop(e); onToggleStart() }}
          title={isStartScene ? 'Startscene entfernen' : 'Als Startscene festlegen'}
          style={{
            flexShrink: 0,
            width: 20,
            height: 20,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            color: isStartScene ? '#f59e0b' : '#6b7280',
            fontSize: 16,
            lineHeight: 1,
            opacity: isStartScene ? 1 : showHoverActions ? 0.9 : 0.45,
            transition: 'opacity 0.12s, color 0.12s',
          }}
        >
          {isStartScene ? '★' : '☆'}
        </button>

        {/* Title + Meta (zentrale Spalte) */}
        <div style={{ flexGrow: 1, flexShrink: 1, flexBasis: 0, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {editing ? (
            <input
              autoFocus
              value={title}
              onClick={stop}
              onChange={e => setTitle(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  updateScene(scene.id, { title })
                  setEditing(false)
                }
                if (e.key === 'Escape') { setTitle(scene.title); setEditing(false) }
              }}
              onBlur={() => { updateScene(scene.id, { title }); setEditing(false) }}
              className="ghost-field__input"
              style={{
                width: '100%',
                fontSize: 13,
                fontWeight: 600,
                background: '#ffffff',
                border: '1px solid #3b82f6',
                borderRadius: 4,
                padding: '2px 6px',
                color: '#111827',
                outline: 'none',
              }}
            />
          ) : (
            <div
              onDoubleClick={(e) => { stop(e); setEditing(true) }}
              title="Doppelklick zum Umbenennen"
              style={{
                fontSize: 13,
                fontWeight: 600,
                lineHeight: 1.3,
                color: '#000000',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {scene.title}
            </div>
          )}

          {/* Meta-Zeile (kompakt, eine Linie) */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 11,
              color: '#6b7280',
              lineHeight: 1.2,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                padding: '1px 6px',
                borderRadius: 3,
                background:
                  viewMode === 'grundriss' ? 'rgba(34, 197, 94, 0.18)'
                  : viewMode === 'schnitt' ? 'rgba(168, 85, 247, 0.18)'
                  : 'rgba(59, 130, 246, 0.18)',
                color:
                  viewMode === 'grundriss' ? '#15803d'
                  : viewMode === 'schnitt' ? '#7e22ce'
                  : '#1d4ed8',
                flexShrink: 0,
              }}
            >
              {viewLabel}
            </span>
            {scene.viewTargetName && (
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {scene.viewTargetName}
              </span>
            )}
            {scene.annotationIds.length > 0 && (
              <>
                <span style={{ opacity: 0.4 }}>·</span>
                <span>{scene.annotationIds.length} Notizen</span>
              </>
            )}
            {scene.viewerState?.clipBox && (
              <>
                <span style={{ opacity: 0.4 }}>·</span>
                <span>ClipBox</span>
              </>
            )}
          </div>
        </div>

        {/* Rechte Aktionsleiste — rechtsbündig */}
        <div
          style={{
            marginLeft: 'auto',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 4,
          }}
        >
          {/* Details Button — sichtbar bei Hover oder wenn offen */}
          <button
            data-element="btn-scene-details"
            onClick={(e) => { stop(e); onToggleDetails() }}
            title={isDetailsOpen ? 'Details schließen' : 'Details anzeigen'}
            aria-pressed={isDetailsOpen}
            style={{
              flexShrink: 0,
              height: 26,
              padding: '0 10px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 11,
              fontWeight: 600,
              background: isDetailsOpen ? '#3b82f6' : 'rgba(59, 130, 246, 0.12)',
              color: isDetailsOpen ? '#ffffff' : '#1d4ed8',
              border: '1px solid ' + (isDetailsOpen ? '#3b82f6' : 'rgba(59, 130, 246, 0.45)'),
              borderRadius: 5,
              cursor: 'pointer',
              opacity: isDetailsOpen || showHoverActions ? 1 : 0,
              pointerEvents: isDetailsOpen || showHoverActions ? 'auto' : 'none',
              transition: 'opacity 0.12s, background 0.12s',
              whiteSpace: 'nowrap',
            }}
          >
            <span style={{ fontSize: 12, lineHeight: 1 }}>ⓘ</span>
            <span>Details</span>
          </button>

          {/* ⋯ Menu */}
          <div ref={menuRef} style={{ position: 'relative' }}>
            <button
              data-element="btn-scene-menu"
              onClick={(e) => { stop(e); setMenuOpen(v => !v) }}
              title="Mehr Aktionen"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              style={{
                flexShrink: 0,
                width: 26,
                height: 26,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: menuOpen ? 'rgba(0,0,0,0.08)' : 'transparent',
                border: '1px solid ' + (menuOpen ? 'rgba(0,0,0,0.15)' : 'transparent'),
                borderRadius: 5,
                cursor: 'pointer',
                color: '#4b5563',
                fontSize: 16,
                lineHeight: 1,
                opacity: menuOpen || showHoverActions ? 1 : 0,
                pointerEvents: menuOpen || showHoverActions ? 'auto' : 'none',
                transition: 'opacity 0.12s, background 0.12s',
              }}
            >
              ⋯
            </button>

            {menuOpen && (
              <div
                role="menu"
                onClick={stop}
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  right: 0,
                  minWidth: 180,
                  background: '#1f2937',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 6,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                  padding: 4,
                  zIndex: 50,
                }}
              >
                <MenuItem
                  icon="✎"
                  label="Umbenennen"
                  onClick={() => { setMenuOpen(false); setEditing(true) }}
                />
                {isActive && (
                  <MenuItem
                    icon="↻"
                    label="View aktualisieren"
                    onClick={() => { setMenuOpen(false); onUpdateView() }}
                  />
                )}
                <MenuItem
                  icon={isStartScene ? '☆' : '★'}
                  label={isStartScene ? 'Startscene entfernen' : 'Als Startscene'}
                  onClick={() => { setMenuOpen(false); onToggleStart() }}
                />
                <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '4px 0' }} />
                <MenuItem
                  icon="✕"
                  label="Löschen"
                  danger
                  onClick={() => { setMenuOpen(false); onDelete() }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function MenuItem({ icon, label, onClick, danger = false }: {
  icon: string
  label: string
  onClick: () => void
  danger?: boolean
}) {
  const [hover, setHover] = useState(false)
  return (
    <button
      role="menuitem"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '7px 10px',
        background: hover ? (danger ? 'rgba(248, 113, 113, 0.15)' : 'rgba(255,255,255,0.06)') : 'transparent',
        border: 'none',
        borderRadius: 4,
        cursor: 'pointer',
        fontSize: 12,
        fontWeight: 500,
        color: danger ? '#f87171' : '#e5e7eb',
        textAlign: 'left',
        transition: 'background 0.1s',
      }}
    >
      <span style={{ width: 14, textAlign: 'center', opacity: 0.8 }}>{icon}</span>
      <span>{label}</span>
    </button>
  )
}

// ─── SceneDetailsTable ───────────────────────────────────────────────────────

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return String(value)
    if (Math.abs(value) >= 0.001 && Math.abs(value) < 1e9) {
      return Number.isInteger(value) ? String(value) : value.toFixed(4)
    }
    return value.toExponential(3)
  }
  if (typeof value === 'string') return value || '—'
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]'
    if (value.every(v => typeof v === 'number')) {
      return `[${value.map(v => formatValue(v)).join(', ')}]`
    }
    return `[${value.length} Einträge]`
  }
  return JSON.stringify(value)
}

function flattenObject(obj: any, prefix = ''): Array<[string, unknown]> {
  const rows: Array<[string, unknown]> = []
  if (obj === null || obj === undefined) return rows
  if (typeof obj !== 'object') {
    rows.push([prefix || '(value)', obj])
    return rows
  }
  if (Array.isArray(obj)) {
    // Arrays of primitives → render inline
    if (obj.every(v => typeof v !== 'object' || v === null)) {
      rows.push([prefix, obj])
      return rows
    }
    obj.forEach((v, i) => {
      rows.push(...flattenObject(v, `${prefix}[${i}]`))
    })
    return rows
  }
  for (const [key, value] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${key}` : key
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      rows.push(...flattenObject(value, path))
    } else if (Array.isArray(value) && value.some(v => v !== null && typeof v === 'object')) {
      rows.push(...flattenObject(value, path))
    } else {
      rows.push([path, value])
    }
  }
  return rows
}

function SceneDetailsTable({ scene, anchorRef, onClose }: {
  scene: Scene
  anchorRef: React.RefObject<HTMLDivElement>
  onClose: () => void
}) {
  const [pos, setPos] = useState<{ top: number; left: number; height: number } | null>(null)

  useLayoutEffect(() => {
    function updatePos() {
      const el = anchorRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      const width = 460
      const margin = 8
      let left = r.right + margin
      // Falls rechts kein Platz: links neben dem Sidebar einblenden
      if (left + width > window.innerWidth - 8) {
        left = Math.max(8, r.left - width - margin)
      }
      setPos({ top: r.top, left, height: r.height })
    }
    updatePos()
    window.addEventListener('resize', updatePos)
    window.addEventListener('scroll', updatePos, true)
    const id = window.setInterval(updatePos, 250) // robust gegen layout-shifts (panel öffnen/schließen)
    return () => {
      window.removeEventListener('resize', updatePos)
      window.removeEventListener('scroll', updatePos, true)
      window.clearInterval(id)
    }
  }, [anchorRef])

  const topLevel: Array<[string, unknown]> = [
    ['id', scene.id],
    ['title', scene.title],
    ['description', scene.description],
    ['cloudId', scene.cloudId],
    ['viewMode', scene.viewMode],
    ['viewTargetName', scene.viewTargetName],
    ['isStartScene', scene.isStartScene],
    ['createdAt', scene.createdAt],
    ['updatedAt', scene.updatedAt],
    ['activeCloudIds', scene.activeCloudIds],
    ['annotationIds', scene.annotationIds],
    ['auditIds', scene.auditIds],
  ]
  const viewerRows = scene.viewerState ? flattenObject(scene.viewerState, '') : []

  if (!pos) return null

  return createPortal(
    <div
      data-component="SceneDetailsTable"
      className="ghost-panel"
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        width: 460,
        maxHeight: Math.max(pos.height, window.innerHeight - pos.top - 16),
        overflow: 'auto',
        background: '#ffffff',
        color: '#111827',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: 8,
        boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
        zIndex: 100000,
        pointerEvents: 'auto',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 12px',
          borderBottom: '1px solid rgba(0,0,0,0.1)',
          position: 'sticky',
          top: 0,
          background: '#ffffff',
          color: '#111827',
          zIndex: 1,
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 13 }}>
          Scene Details · <span style={{ opacity: 0.7 }}>{scene.title}</span>
        </div>
        <button
          onClick={onClose}
          className="ghost-action-btn"
          title="Details schließen"
        >
          ✕
        </button>
      </div>

      <style>{`.sdt-row:hover { background: rgba(59,130,246,0.08); }`}</style>
      <div style={{ padding: '8px 12px' }}>
        {scene.thumbnailMedium && (
          <div style={{ margin: '4px 0 8px', borderRadius: 6, overflow: 'hidden', background: '#1f2937' }}>
            <img
              src={scene.thumbnailMedium}
              alt={`Thumbnail – ${scene.title}`}
              style={{ width: '100%', display: 'block', objectFit: 'cover', maxHeight: 180 }}
            />
          </div>
        )}
        <div style={{ fontSize: 11, opacity: 0.6, margin: '6px 0 4px', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Scene
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <tbody>
            {topLevel.map(([key, value]) => (
              <tr key={key} data-scene-field={key} className="sdt-row" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                <td style={{ padding: '4px 8px 4px 0', opacity: 0.7, verticalAlign: 'top', width: '40%', wordBreak: 'break-word' }}>
                  {key}
                </td>
                <td style={{ padding: '4px 0', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {formatValue(value)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {viewerRows.length > 0 && (
          <>
            <div style={{ fontSize: 11, opacity: 0.6, margin: '14px 0 4px', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              viewerState
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <tbody>
                {viewerRows.map(([key, value], i) => (
                  <tr key={`${key}-${i}`} data-viewer-field={key} className="sdt-row" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                    <td style={{ padding: '4px 8px 4px 0', opacity: 0.7, verticalAlign: 'top', width: '50%', wordBreak: 'break-word' }}>
                      {key}
                    </td>
                    <td style={{ padding: '4px 0', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {formatValue(value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <div style={{ fontSize: 11, opacity: 0.6, margin: '14px 0 4px', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Raw JSON
        </div>
        <pre
          style={{
            margin: 0,
            padding: 8,
            background: '#f3f4f6',
            color: '#111827',
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: 4,
            fontSize: 11,
            maxHeight: 240,
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}
        >
          {JSON.stringify(scene, null, 2)}
        </pre>
      </div>
    </div>,
    document.body
  )
}

// ─── Main Panel ──────────────────────────────────────────────────────────────

export function ScenePanel({ currentProjectId, currentCloudId = null, projectCloudIds = [] }: { currentProjectId: string | null; currentCloudId?: string | null; projectCloudIds?: string[] }) {
  const scenes = useSceneStore(s => s.scenes)
  const activeSceneId = useSceneStore(s => s.activeSceneId)
  const createScene = useSceneStore(s => s.createScene)
  const deleteScene = useSceneStore(s => s.deleteScene)
  const activateScene = useSceneStore(s => s.activateScene)
  const deactivateScene = useSceneStore(s => s.deactivateScene)
  const updateScene = useSceneStore(s => s.updateScene)
  const setStartScene = useSceneStore(s => s.setStartScene)
  const loadFromServer = useSceneStore(s => s.loadFromServer)
  const activateStartScene = useSceneStore(s => s.activateStartScene)

  const [detailsSceneId, setDetailsSceneId] = useState<string | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => { loadFromServer() }, [loadFromServer])

  // Expose startscene activation for script.js to call after project load
  useEffect(() => {
    window._activateStartScene = (projectId?: string) => {
      activateStartScene(projectId)
    }
    return () => { delete (window as any)._activateStartScene }
  }, [activateStartScene])

  // Expose setStartSceneFromViewer for context menu in script.js
  useEffect(() => {
    ;(window as any)._setStartSceneFromViewer = () => {
      const vs = captureViewerState()
      if (!vs) return
      const projectId = (window as any).currentProject?.id || ''
      const cloudId = (window as any).currentCloudConfig?.id || ''
      const viewMode = ((window as any).getCurrentViewMode?.() || 'modell') as 'modell' | 'grundriss' | 'schnitt'
      const viewTargetName = (window as any).getActiveViewTargetName?.() || undefined

      // Prüfen ob bereits eine Startscene existiert → updaten statt neu anlegen
      const existing = useSceneStore.getState().scenes.find(
        sc => sc.isStartScene && (!projectId || sc.projectId === projectId)
      )
      if (existing) {
        updateScene(existing.id, { viewerState: vs, viewMode, viewTargetName })
      } else {
        const scene = createScene({
          title: 'Startscene',
          projectId,
          cloudId,
          viewerState: vs,
          viewMode,
          viewTargetName,
        } as any)
        setStartScene(scene.id)
      }
    }
    return () => { delete (window as any)._setStartSceneFromViewer }
  }, [createScene, updateScene, setStartScene])

  // Expose createSceneFromViewer for context menu in script.js
  useEffect(() => {
    ;(window as any)._createSceneFromViewer = () => {
      const vs = captureViewerState()
      if (!vs) return
      const projectId = (window as any).currentProject?.id || ''
      const cloudId = (window as any).currentCloudConfig?.id || ''
      const viewMode = ((window as any).getCurrentViewMode?.() || 'modell') as 'modell' | 'grundriss' | 'schnitt'
      const viewTargetName = (window as any).getActiveViewTargetName?.() || undefined
      const count = useSceneStore.getState().scenes.filter(
        sc => !projectId || sc.projectId === projectId
      ).length
      createScene({
        title: `Scene ${count + 1}`,
        projectId,
        cloudId,
        viewerState: vs,
        viewMode,
        viewTargetName,
      } as any)
    }
    return () => { delete (window as any)._createSceneFromViewer }
  }, [createScene])

  const cloudScenes = useMemo(() =>
    scenes.filter(sc => {
      // Primär: projectId-Match
      if (currentProjectId && sc.projectId === currentProjectId) return true
      // Legacy-Fallback: Scene ohne projectId, aber cloudId gehört zum aktiven Projekt
      if (!sc.projectId && sc.cloudId) {
        if (projectCloudIds.length > 0 && projectCloudIds.includes(sc.cloudId)) return true
        if (currentCloudId && sc.cloudId === currentCloudId) return true
      }
      // Kein aktives Projekt bekannt → Cloud-Fallback
      if (!currentProjectId && currentCloudId) {
        if (projectCloudIds.length > 0) return sc.cloudId ? projectCloudIds.includes(sc.cloudId) : false
        return sc.cloudId === currentCloudId
      }
      return false
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scenes, currentProjectId, currentCloudId, projectCloudIds.join(',')]
  )

  const handleCreate = () => {
    const vs = captureViewerState()
    if (!vs) return
    const viewMode = (window.getCurrentViewMode?.() || 'modell') as 'modell' | 'grundriss' | 'schnitt'
    const viewTargetName = window.getActiveViewTargetName?.() || undefined
    // Thumbnails VOR dem Erstellen capturen → werden mit dem POST mitgesendet
    const thumbs = captureViewerThumbnailPair()
    const mhThumbs = captureViewerThumbnailMediumHigh()
    createScene({
      title: `Scene ${cloudScenes.length + 1}`,
      projectId: currentProjectId ?? undefined,
      cloudId: currentCloudId ?? undefined,
      viewerState: vs,
      viewMode,
      viewTargetName,
      ...(thumbs ? { thumbnailSmall: thumbs.small } : {}),
      ...(mhThumbs ? { thumbnailMedium: mhThumbs.medium, thumbnailHigh: mhThumbs.high } : (thumbs ? { thumbnailMedium: thumbs.medium } : {})),
    } as any)
  }

  const handleUpdateViewerState = (sceneId: string) => {
    const vs = captureViewerState()
    if (!vs) return
    const viewMode = (window.getCurrentViewMode?.() || 'modell') as 'modell' | 'grundriss' | 'schnitt'
    const viewTargetName = window.getActiveViewTargetName?.() || undefined
    updateScene(sceneId, { viewerState: vs, viewMode, viewTargetName })
    // Thumbnail nach View-Update aktualisieren (Viewer ist gerade aktiv)
    try { captureAndStoreSceneThumbnail(sceneId) } catch { /* noop */ }
  }

  // Wenn die ausgewählte Detail-Scene gelöscht wurde → Panel schließen
  useEffect(() => {
    if (detailsSceneId && !scenes.find(s => s.id === detailsSceneId)) {
      setDetailsSceneId(null)
    }
  }, [scenes, detailsSceneId])

  // Wenn das Details-Panel offen ist, beim Wechsel der aktiven Scene mitziehen
  useEffect(() => {
    if (detailsSceneId && activeSceneId && activeSceneId !== detailsSceneId) {
      setDetailsSceneId(activeSceneId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSceneId])

  // Aktuellste Daten der Detail-Scene immer frisch aus dem Store ziehen
  // (so updaten sich auch View-Updates / Umbenennungen live im Panel)
  const detailsScene = detailsSceneId ? scenes.find(s => s.id === detailsSceneId) : null

  return (
    <div
      ref={panelRef}
      data-component="ScenePanel"
      className="ghost-panel"
      style={{ position: 'relative' }}
    >
      {/* Scene List */}
      <div className="ghost-list">
        {cloudScenes.length === 0 ? (
          <div className="ghost-empty">
            Noch keine Scenes erstellt.
            <div className="ghost-empty__hint">
              Eine Scene speichert den aktuellen View (Kamera + ClipBox).
              <br />
              Scenes können in Modell, Grundriss und Schnitt erstellt werden.
              <br />
              Mit ★ als Startscene festlegen.
            </div>
          </div>
        ) : cloudScenes.map(sc => (
          <SceneRow
            key={sc.id}
            scene={sc}
            isActive={sc.id === activeSceneId}
            isStartScene={!!sc.isStartScene}
            isDetailsOpen={sc.id === detailsSceneId}
            onActivate={() => sc.id === activeSceneId ? deactivateScene() : activateScene(sc.id)}
            onUpdateView={() => handleUpdateViewerState(sc.id)}
            onDelete={() => deleteScene(sc.id)}
            onToggleStart={() => setStartScene(sc.isStartScene ? null : sc.id)}
            onToggleDetails={() => setDetailsSceneId(prev => prev === sc.id ? null : sc.id)}
          />
        ))}
      </div>

      {/* Create Button (sticky bottom) */}
      <div className="ghost-footer">
        <button
          data-element="btn-create-scene"
          onClick={handleCreate}
          className="ghost-create-form__btn"
          style={{ width: '100%' }}
        >
          + Neue Scene erstellen
        </button>
      </div>

      {/* Details Panel — öffnet sich rechts neben dem Scenes-Layer (Portal → über Canvas) */}
      {detailsScene && (
        <SceneDetailsTable
          scene={detailsScene}
          anchorRef={panelRef}
          onClose={() => setDetailsSceneId(null)}
        />
      )}
    </div>
  )
}
